/**
 * `<MessageStream>` — section 2 of the B-1 VM Debug pane.
 *
 * The scrollable main body that renders one `<MessageLine>` per
 * captured `VmMessage`. Filters the input by the active filter set
 * (owned by the pane root) and virtualises the rows via
 * react-window's `List` + `useDynamicRowHeight` so 5000+ messages
 * don't melt the DOM.
 *
 * # react-window v2 (TDD §B-1 + cross-cutting "Dependency additions")
 *
 * The TDD spec refers to `VariableSizeList` + `resetAfterIndex` — that
 * is the v1 API. react-window 2.x ships an equivalent capability via
 * `useDynamicRowHeight` that internally calls `setRowHeight(index,
 * size)` and `observeRowElements` to remeasure when an expanded row
 * grows / shrinks. The behaviour the TDD requires (efficient
 * virtualisation with on-the-fly height remeasure on expand /
 * collapse + window resize) is achieved through that v2 hook.
 *
 * Collapsed rows are a fixed 28px; expanded rows are measured by the
 * v2 hook's `observeRowElements` IntersectionObserver under the hood
 * and the cache is invalidated whenever the underlying `messages` /
 * `expansion` keys change (the `key` arg to `useDynamicRowHeight`).
 *
 * # Empty state
 *
 * When no messages have arrived yet, the component renders an inline
 * "No messages yet" string in place of the virtualised list. Same
 * surface, no list mount until there's something to render.
 */

import { useCallback, useMemo, useRef } from 'react';
import { List, useDynamicRowHeight, type RowComponentProps } from 'react-window';
import type { VmMessage, VmMessageKind } from '@volley/crucible-ipc-types/vm-message-stream';
import { MessageLine } from './MessageLine';

export interface MessageStreamProps {
  /** Sorted-by-seq message buffer from `useMessageStream`. */
  messages: VmMessage[];
  /** Set of currently-enabled message kinds. */
  enabledKinds: Set<VmMessageKind>;
  /** Map of seq → expanded state. Parent owns so the virtualised
   *  list can remeasure heights on toggle. */
  expanded: Record<number, boolean>;
  /** Called when the user toggles a row's expand chevron. */
  onToggleExpanded: (seq: number) => void;
  /** True when the hold-to-reveal gate is active. Threaded into each
   *  `<MessageLine>` so the raw payload branch fires consistently. */
  rawActive: boolean;
  /** Optional explicit height — production passes the pane body's
   *  client height; tests can pass a small fixed value. */
  height?: number;
}

/** Default collapsed row height (px). Real expanded rows are measured
 *  by the v2 dynamic-row-height hook. */
const COLLAPSED_ROW_HEIGHT = 28;

/**
 * Props passed to each row by react-window. We use `rowProps` to
 * thread the runtime state through the list without forcing the row
 * component to consume context.
 */
interface RowProps {
  filtered: VmMessage[];
  expanded: Record<number, boolean>;
  onToggleExpanded: (seq: number) => void;
  rawActive: boolean;
}

function MessageRow({
  index,
  style,
  ariaAttributes,
  filtered,
  expanded,
  onToggleExpanded,
  rawActive,
}: RowComponentProps<RowProps>): React.JSX.Element {
  const message = filtered[index];
  const isExpanded = !!expanded[message.seq];
  return (
    <div style={style} {...ariaAttributes}>
      <MessageLine
        message={message}
        expanded={isExpanded}
        onToggleExpanded={() => onToggleExpanded(message.seq)}
        rawActive={rawActive}
      />
    </div>
  );
}

export function MessageStream({
  messages,
  enabledKinds,
  expanded,
  onToggleExpanded,
  rawActive,
  height = 400,
}: MessageStreamProps): React.JSX.Element {
  // Filter once per render. Memoised on the input identity + filter
  // set + expansion map so the row data doesn't churn between
  // unrelated parent re-renders.
  const filtered = useMemo(
    () => messages.filter((m) => enabledKinds.has(m.kind)),
    [messages, enabledKinds],
  );

  // Build a stable cache key for the dynamic row-height hook. When
  // `expanded` flips for any seq, the key changes and the hook
  // remeasures from scratch — equivalent to the v1 `resetAfterIndex`
  // contract from the TDD (but driven by the v2 API).
  const expansionFingerprint = useMemo(() => {
    // Stringify the expansion map deterministically — only seqs that
    // are currently expanded contribute, so the fingerprint changes
    // whenever a row is opened or closed. Filtering to truthy values
    // avoids churn from `false`-valued keys that stick around in the
    // map after a collapse.
    const open = Object.keys(expanded)
      .filter((k) => expanded[Number(k)])
      .sort();
    return `${filtered.length}:${open.join(',')}`;
  }, [filtered.length, expanded]);

  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: COLLAPSED_ROW_HEIGHT,
    key: expansionFingerprint,
  });

  const rowProps = useMemo<RowProps>(
    () => ({ filtered, expanded, onToggleExpanded, rawActive }),
    [filtered, expanded, onToggleExpanded, rawActive],
  );

  // Capture the body ref so the empty-state surface mounts with the
  // same outer shape as the populated list — keeps DOM diffs cheap on
  // first-message arrival.
  const bodyRef = useRef<HTMLDivElement | null>(null);

  if (filtered.length === 0) {
    return (
      <div
        ref={bodyRef}
        data-testid="vm-debug-message-stream-empty"
        className="flex flex-1 items-center justify-center px-3 py-6 text-xs text-white/30"
      >
        {messages.length === 0 ? 'No messages captured yet.' : 'All messages filtered out.'}
      </div>
    );
  }

  return (
    <div
      ref={bodyRef}
      data-testid="vm-debug-message-stream"
      data-row-count={filtered.length}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <List
        rowCount={filtered.length}
        rowHeight={dynamicRowHeight}
        rowComponent={MessageRow}
        rowProps={rowProps}
        defaultHeight={height}
        overscanCount={6}
        style={{ height }}
      />
    </div>
  );
}

/** Test-only — exposed so `MessageStream.test.tsx` can verify the
 *  expansion fingerprint contract without binding to react-window's
 *  internal cache state. */
export function __computeExpansionFingerprintForTests(
  filteredLength: number,
  expanded: Record<number, boolean>,
): string {
  const open = Object.keys(expanded)
    .filter((k) => expanded[Number(k)])
    .sort();
  return `${filteredLength}:${open.join(',')}`;
}

/** Suppress unused-import lint when tests import the row component
 *  separately. */
export { MessageRow };
